import { Button, message, Modal, Form, Input, Tooltip } from 'antd';
import { useState } from 'react';
import { useUpdateBestDeal, useDeleteBestDeal } from '../hooks/useBestDeals';
import { DeleteOutlined } from "@ant-design/icons";

const BestDealCard = ({ bestDeal }) => {
    const { product, description, tag, id } = bestDeal;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const { mutate: updateBestDeal, isLoading } = useUpdateBestDeal();
    const { mutate: deleteBestDeal } = useDeleteBestDeal();

    const handleEditBestDeal = () => {
        setIsModalVisible(true);
        form.setFieldsValue({ tag, description });
    };

    const handleSubmit = (values) => {
        updateBestDeal({
            id,
            tag: values.tag,
            description: values.description
        }, {
            onSuccess: () => {
                message.success('Best deal updated successfully!');
                setIsModalVisible(false);
            },
            onError: () => {
                message.error('Failed to update best deal');
            }
        });
    };

    const handleDeleteBestDeal = (bestDealID) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this deal?',
            content: 'This action will remove the product from best deals',
            okText: 'Yes, delete it',
            okType: 'danger',
            cancelText: 'No, keep it',
            className: '[&_.ant-modal-content]:!rounded-2xl',
            onOk() {
                deleteBestDeal(bestDealID, {
                    onSuccess: () => message.warning('Deal deleted'),
                    onError: (err) => message.error(err.message)
                });
            }
        });
    };

    return (
        product?.image_paths?.length ? (
            <div className="group p-4 rounded-xl shadow-lg bg-white flex flex-col h-full transition-all duration-300 hover:shadow-xl">
                <div className="w-full mb-3 relative overflow-hidden rounded-lg aspect-square">
                    <img
                        alt={product.name}
                        src={product.image_paths[0]}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                        <Tooltip title="Delete deal">
                            <div
                                className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                                onClick={() => handleDeleteBestDeal(id)}
                            >
                                <DeleteOutlined className="text-red-500 hover:text-red-600" />
                            </div>
                        </Tooltip>
                    </div>
                    <div className="absolute bottom-2 left-2 bg-white/90 px-3 py-1 rounded-full text-sm font-medium">
                        <span className={product.left_in_stock > 5 ? "text-green-600" : "text-red-600"}>
                            {product.left_in_stock} in stock
                        </span>
                    </div>
                </div>

                <div className="p-2 flex-grow">
                    <h5 className="text-lg font-bold text-gray-800 line-clamp-2 mb-2">
                        {product.name}
                    </h5>
                    <div className="text-xl font-semibold text-yellow-600 mb-3">
                        {product.price} br
                    </div>
                    <div className="mb-2">
                        <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
                            {tag}
                        </span>
                    </div>
                    <p className="text-gray-600 line-clamp-3">{description}</p>
                </div>

                <Button
                    onClick={handleEditBestDeal}
                    className="!bg-yellow-500 !text-white !rounded-lg !hover:bg-yellow-600 !mt-auto"
                >
                    Update Best Deal
                </Button>

                <Modal
                    title="Update Best Deal"
                    visible={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={null}
                    centered
                    className="[&_.ant-modal-content]:!rounded-2xl [&_.ant-modal-content]:!p-6 [&_.ant-modal-header]:!border-0 [&_.ant-modal-title]:!text-xl [&_.ant-modal-title]:!font-bold"
                    getContainer={false}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        <Form.Item
                            label="Tag"
                            name="tag"
                            rules={[{ required: true, message: 'Please enter a tag' }]}
                            className="mb-6"
                        >
                            <Input
                                placeholder="e.g. Holiday Discount"
                                className="!rounded-lg !py-2 hover:!border-yellow-500 focus:!border-yellow-600 !shadow-sm"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Description"
                            name="description"
                            rules={[{ required: true, message: 'Please enter a description' }]}
                            className="mb-6"
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="e.g. Use this chance and buy this item"
                                className="!rounded-lg hover:!border-yellow-500 focus:!border-yellow-600 !shadow-sm"
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button
                                htmlType="submit"
                                loading={isLoading}
                                block
                                className="!bg-yellow-500 !text-white !rounded-lg !h-10 !font-medium hover:!bg-yellow-600 !border-none !shadow-sm"
                            >
                                Update Deal
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        ) : null
    );
};

export default BestDealCard;